/**
 * This file contains authentication parameters. Contents of this file
 * is roughly the same across other MSAL.js libraries. These parameters
 * are used to initialize Angular and MSAL Angular configurations in
 * in app.module.ts file.
 */

import {
  LogLevel,
  Configuration,
  BrowserCacheLocation
} from '@azure/msal-browser';
import { environment } from 'src/environments/environment';

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

/**
 * Enter here the user flows and custom policies for your B2C application,
 * To learn more about user flows, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
// Dynamically extract the B2C tenant name from the domain
const tenantName = environment.authorityDomain.split('.')[0]; // e.g., 'evideb2cdev' or 'myevide'

// Dev (evideb2cdev) and production (myevide) sign in and reset passwords through the B2C custom policies
// (policy files in evide-claude-docs/b2c-custom-policies; production switched per the production runbook).
// Any other tenant falls back to the user flows. Rolling back = removing the tenant from this check.
const usesCustomPolicies = tenantName === 'evideb2cdev' || tenantName === 'myevide';
// Password reset / change (pen test EVI-003-003): refuses common and breached passwords.
const resetPasswordPolicy = usesCustomPolicies ? 'B2C_1A_PasswordReset' : 'B2C_1_reset_password';
// Sign-in (pen test EVI-003-002): authenticator code attempt limit (6 attempts -> 30-minute lockout).
// Everyone on a tenant signs in once more after it is switched.
const signInPolicy = usesCustomPolicies ? 'B2C_1A_SignIn' : 'B2C_1_signin_in';

// Organisation sign-in (SSO per licence): the separate sign-in policy holding every client's work-login
// provider, per tenant. null = no email page and no SSO on that tenant. Production has the email page from
// the 2026-11-02 release: 'B2C_1A_SignIn_SSO' is the name the production SSO policy will have once the first
// client is added (release guide section 12). Until it is uploaded no production licence may have
// organisation sign-in allowed - the email page only routes to that policy for an SSO licence's domains, so
// with none allowed every production sign-in still goes to the usual password page.
const ssoSignInPolicy: string | null =
  tenantName === 'evideb2cdev' ? 'B2C_1A_SignIn_SSOPOC'
    : tenantName === 'myevide' ? 'B2C_1A_SignIn_SSO'
      : null;

/**
 * Email-first sign-in (SSO implementation plan piece 6a): Evide's own page asks for the email, then the
 * API says whether it belongs to an organisation-sign-in licence. On only where the SSO policy exists;
 * off means the app redirects straight to the B2C sign-in page as it always has.
 */
export const emailFirstSignIn = ssoSignInPolicy !== null;

export const b2cPolicies = {
  names: {
    signUpSignIn: signInPolicy,
    signUp: 'B2C_1_signin_up',
    editProfile: 'B2C_1_edit_profile',
    resetPassword: resetPasswordPolicy,
    ssoSignIn: ssoSignInPolicy
  },
  authorities: {
    signUpSignIn: {
      authority: `https://${environment.authorityDomain}/${tenantName}.onmicrosoft.com/${signInPolicy}`
    },
    signUp: {
      authority: `https://${environment.authorityDomain}/${tenantName}.onmicrosoft.com/B2C_1_signin_up`
    },
    editProfile: {
      authority: `https://${environment.authorityDomain}/${tenantName}.onmicrosoft.com/B2C_1_edit_profile`
    },
    resetPassword: {
      authority: `https://${environment.authorityDomain}/${tenantName}.onmicrosoft.com/${resetPasswordPolicy}`
    },
    ssoSignIn: {
      authority: ssoSignInPolicy
        ? `https://${environment.authorityDomain}/${tenantName}.onmicrosoft.com/${ssoSignInPolicy}`
        : null
    }
  },
  authorityDomain: environment.authorityDomain
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: environment.clientId, // This is the ONLY mandatory field that you need to supply.
    authority: b2cPolicies.authorities.signUpSignIn.authority, // Defaults to "https://login.microsoftonline.com/common"
    knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
    redirectUri: environment.redirectUri, //'http://localhost:4200/', // Points to window.location.origin by default. You must register this URI on Azure portal/App Registration.
    postLogoutRedirectUri: environment.postLogoutRedirectUri //'http://localhost:4200/' // Points to window.location.origin by default.
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage, // localStorage so multiple tabs share the session. Defense in depth via 60-min access token + refresh rotation + B2C session revocation on logout/password change.
    storeAuthStateInCookie: isIE // Set this to "true" if you are having issues on IE11 or Edge. Remove this line to use Angular Universal
  },
  system: {
    /**
     * Below you can configure MSAL.js logs. For more information, visit:
     * https://docs.microsoft.com/azure/active-directory/develop/msal-logging-js
     */
    loggerOptions: {
      loggerCallback(logLevel: LogLevel, message: string) {},
      logLevel: LogLevel.Verbose,
      piiLoggingEnabled: false
    }
  }
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */

/**
 * Scope exposed on our own B2C app registration ("Expose an API" > access).
 * Requesting it yields an access token (aud = our clientId, so the backend's
 * existing ValidAudiences accepts it) that MSAL can renew silently via the
 * refresh token — the ID token alone cannot drive silent renewal reliably.
 * Empty when the environment doesn't configure a scope; everything then
 * falls back to the pre-existing ID-token behaviour.
 */
const configuredApiScope = (environment as { apiScope?: string }).apiScope;
export const apiScopes: string[] = configuredApiScope ? [configuredApiScope] : [];

export const loginRequest = {
  scopes: ['openid', 'profile', ...apiScopes]
};

/**
 * An optional silentRequest object can be used to achieve silent SSO
 * between applications by providing a "loginHint" property (such as a username). For more, visit:
 * https://learn.microsoft.com/en-us/azure/active-directory/develop/msal-js-sso#sso-between-different-apps
 * If you do not receive the username claim in ID tokens, see also:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/FAQ.md#why-is-getaccountbyusername-returning-null-even-though-im-signed-in
 */
export const silentRequest = {
  //scopes: [],
  scopes: ['openid', 'profile'],
  loginHint: 'example@domain.net'
};
